import { memo, useEffect, useState } from 'react';

import { ChartWithBarsWrapper } from 'PDF/components/Charts/ChartWithBars';
import { IImageChart } from 'PDF/components/Charts/types';
import { ProcessStages } from 'PDF/components/ProcessStages/ProcessStages';
import { PDF_SECTIONS } from 'PDF/constants/sections';
import { prepareSectionId } from 'PDF/helpers/pdfMapping';
import { getProductDescriptionData } from 'PDF/helpers/productDescriptionMapping';
import { useGenerateImageFromElement } from 'PDF/hooks/useGenerateImageFromElement';
import { IPdfPageProps } from 'PDF/types/pdf';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlanChapters, getBusinessPlanType } from 'store/useBusinessToolStore/selectors';

import { useExamplePreview } from 'hooks/useExamplePreview';

import {
  getBuildingsPremisesDetails,
  getEquipments,
  getMagazineItems,
  getProcessStages,
  getProductionPlans,
  getSupplierOverviewData,
} from './mapping';
import { IBusinessOperationsPdfData } from './types';

export const BusinessOperations = memo(({ saveSectionProps, sectionPicture }: IPdfPageProps) => {
  const businessPlanChapters = useBusinessToolStore(getBusinessPlanChapters);
  const businessPlanType = useBusinessToolStore(getBusinessPlanType);
  const { isExamplePage } = useExamplePreview();

  const magazineItems = getMagazineItems(businessPlanChapters, businessPlanType, isExamplePage);
  const buildingsPremisesDetails = getBuildingsPremisesDetails(businessPlanChapters, isExamplePage);
  const { equipmentGroups, plannedPurchases, assetGroups, futureAcquisitions } = getEquipments(
    businessPlanChapters,
    isExamplePage,
  );
  const { processStages, isVisible } = getProcessStages(businessPlanChapters);
  const supplierOverviewData = getSupplierOverviewData(businessPlanChapters, isExamplePage);
  const productionPlans = getProductionPlans(businessPlanChapters, isExamplePage);
  const productAndServicesData = getProductDescriptionData(businessPlanChapters);

  const [portal, processStagesImgSrc] = useGenerateImageFromElement(
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <ProcessStages data={processStages} />
    </div>,
  );
  const [productionPlanImages, setProductionPlanImages] = useState<IImageChart[]>([]);

  const updateProductionPlanImages = (title: string, src: string) => {
    setProductionPlanImages((prevState) => [...prevState, { title, src }]);
  };

  useEffect(() => {
    if (processStagesImgSrc && sectionPicture) {
      const processStagesImg = isVisible ? processStagesImgSrc : '';
      const data: IBusinessOperationsPdfData = {
        buildingsPremisesDetails,
        equipmentGroups,
        plannedPurchases,
        assetGroups,
        futureAcquisitions,
        processStagesImg,
        supplierOverviewData,
        productionPlanImages,
        processStagesNumber: processStages.length,
        ...productAndServicesData,
      };
      saveSectionProps(prepareSectionId(businessPlanType, PDF_SECTIONS.BUSINESS_OPERATIONS), {
        magazineItems,
        data,
      });
    }
  }, [processStagesImgSrc, sectionPicture]);

  return (
    <>
      {portal}
      {productionPlans.map((item, index) => (
        <ChartWithBarsWrapper
          key={index}
          aspectRatio={2.5}
          withDataLabeInsideBar
          info={item}
          updateState={updateProductionPlanImages}
        />
      ))}
    </>
  );
});
